import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowDown, Sun, Grid3x3, Sprout, Zap } from "lucide-react";
import { Reveal, KineticLines } from "../components/Reveal";
import { EditorialMarquee } from "../components/EditorialMarquee";
import { CTASection } from "../components/CTASection";
import { SolarCalculator } from "../components/SolarCalculator";
import { SERVICES } from "../data/site";

const STATS = [
  { value: "5+", label: "Yıl Mühendislik Tecrübesi" },
  { value: "%100", label: "Anahtar Teslim Kurulum" },
  { value: "25 Yıl", label: "Panel Verim Garantisi" },
  { value: "0₺", label: "Keşif & Fizibilite" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-screen min-h-[680px] flex items-end overflow-hidden grain">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src="https://images.pexels.com/photos/17965455/pexels-photo-17965455.jpeg"
          alt="Güneş panelleri"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/60 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 w-full max-w-[1400px] mx-auto px-5 sm:px-8 pb-20 sm:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-12 bg-[#5CC528]" />
          <span className="text-[#5CC528] text-xs sm:text-sm uppercase tracking-[0.25em]">
            Güneş Enerji Santralleri · Mühendislik
          </span>
        </motion.div>

        <h1 className="font-display font-light text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.92] text-white max-w-5xl">
          <KineticLines
            lines={["Çatınızdaki her m²'yi", ""]}
            lineClassName=""
          />
          <span className="block overflow-hidden">
            <motion.span
              className="block text-[#5CC528] text-glow"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              enerjiye çevirin.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-8 text-neutral-300 text-lg sm:text-xl max-w-xl leading-relaxed"
        >
          Renewolt Enerji; çatı ve arazi GES, tarımsal sulama, öz tüketim ve EV şarj
          çözümlerinde uçtan uca mühendislik sunar.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <Link
            to="/hesaplama"
            data-testid="hero-calc-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#5CC528] text-black font-semibold px-8 py-4 rounded-full hover:bg-[#4AA020] transition-colors duration-200"
          >
            Tasarrufunu Hesapla <ArrowUpRight size={18} />
          </Link>
          <Link
            to="/hizmetler"
            data-testid="hero-services-btn"
            className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold px-8 py-4 rounded-full hover:border-[#5CC528] hover:text-[#5CC528] transition-colors duration-200"
          >
            Hizmetlerimiz
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 right-8 z-10 hidden sm:flex items-center gap-2 text-neutral-400 text-xs uppercase tracking-widest"
      >
        <ArrowDown size={14} className="animate-bounce" /> Kaydır
      </motion.div>
    </section>
  );
};

const iconMap = { Sun, Grid3x3, Sprout, Zap };

export default function Home() {
  const featured = SERVICES.slice(0, 4);
  return (
    <>
      <Hero />

      {/* Stats */}
      <section className="relative bg-[#0A0A0A] border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[#5CC528]">{s.value}</div>
              <div className="text-sm text-neutral-400 mt-2">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      <EditorialMarquee />

      {/* Intro / manifesto teaser */}
      <section className="relative bg-[#0A0A0A] py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="text-[#5CC528] text-sm uppercase tracking-[0.25em]">Neden Renewolt?</p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <p className="font-display font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-white">
                Her proje bir <span className="text-[#5CC528]">mühendislik</span> problemidir.
                Biz güneşlenme verilerinden çatı statiğine, teşviklerden şebeke bağlantısına
                kadar tüm süreci sizin için <span className="text-[#5CC528]">uçtan uca</span> yönetiyoruz.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Featured services */}
      <section className="relative bg-[#0A0A0A] pb-24 sm:pb-32">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between mb-12">
            <Reveal>
              <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tighter text-white">Hizmetlerimiz</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/hizmetler" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-neutral-300 hover:text-[#5CC528] transition-colors">
                Tümünü Gör <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.id} delay={i * 0.08}>
                  <Link
                    to="/hizmetler"
                    data-testid={`home-service-${s.id}`}
                    className="group block h-full p-7 rounded-2xl bg-[#121212] border border-white/10 hover:border-[#5CC528]/40 transition-colors duration-300"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#5CC528]/10 flex items-center justify-center mb-6 group-hover:bg-[#5CC528] transition-colors duration-300">
                      <Icon size={22} className="text-[#5CC528] group-hover:text-black transition-colors duration-300" />
                    </div>
                    <h3 className="font-display font-semibold text-xl tracking-tight text-white mb-2">{s.title}</h3>
                    <p className="text-sm text-neutral-400 leading-relaxed">{s.desc}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Calculator teaser */}
      <section className="relative bg-black py-24 sm:py-32 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <Reveal>
              <p className="text-[#5CC528] text-sm uppercase tracking-[0.25em] mb-4">GES Hesaplama Aracı</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-light text-4xl sm:text-6xl tracking-tighter text-white leading-[0.95]">
                Cihazlarını seç,<br />tasarrufunu <span className="text-[#5CC528]">gör.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <SolarCalculator />
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
